import * as React from 'react';
import { cn } from '@/lib/utils';

export interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  strokeColor?: string;
  fillColor?: string;
  strokeWidth?: number;
  showArea?: boolean;
  className?: string;
  trend?: 'up' | 'down' | 'neutral';
}

export function Sparkline({
  data,
  width = 80,
  height = 24,
  strokeColor,
  fillColor,
  strokeWidth = 1.5,
  showArea = true,
  className,
  trend,
}: SparklineProps) {
  const svgRef = React.useRef<SVGSVGElement>(null);

  const normalizedData = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    const validData = data.filter((v) => typeof v === 'number' && Number.isFinite(v));
    if (validData.length === 0) return [];
    return validData;
  }, [data]);

  const pathData = React.useMemo(() => {
    if (normalizedData.length < 2) return { line: '', area: '' };

    const min = Math.min(...normalizedData);
    const max = Math.max(...normalizedData);
    const range = max - min || 1;
    const padding = 2;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    const points = normalizedData.map((value, index) => {
      const x = padding + (index / (normalizedData.length - 1)) * chartWidth;
      const y = padding + chartHeight - ((value - min) / range) * chartHeight;
      return { x, y };
    });

    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

    const areaPath = `${linePath} L ${points[points.length - 1].x} ${height - padding} L ${padding} ${height - padding} Z`;

    return { line: linePath, area: areaPath };
  }, [normalizedData, width, height]);

  const resolvedTrend = React.useMemo(() => {
    if (trend) return trend;
    if (normalizedData.length < 2) return 'neutral';
    const first = normalizedData[0];
    const last = normalizedData[normalizedData.length - 1];
    if (last > first * 1.001) return 'up';
    if (last < first * 0.999) return 'down';
    return 'neutral';
  }, [normalizedData, trend]);

  const colors = React.useMemo(() => {
    if (strokeColor && fillColor) {
      return { stroke: strokeColor, fill: fillColor };
    }

    switch (resolvedTrend) {
      case 'up':
        return {
          stroke: strokeColor ?? '#10B981',
          fill: fillColor ?? 'rgba(16, 185, 129, 0.15)',
        };
      case 'down':
        return {
          stroke: strokeColor ?? '#EF4444',
          fill: fillColor ?? 'rgba(239, 68, 68, 0.15)',
        };
      default:
        return {
          stroke: strokeColor ?? '#6B7280',
          fill: fillColor ?? 'rgba(107, 114, 128, 0.1)',
        };
    }
  }, [resolvedTrend, strokeColor, fillColor]);

  if (normalizedData.length < 2) {
    return (
      <div
        className={cn('flex items-center justify-center text-slate-300', className)}
        style={{ width, height }}
        aria-label="No data available for sparkline"
      >
        <svg width={width} height={height} aria-hidden="true">
          <line
            x1={4}
            y1={height / 2}
            x2={width - 4}
            y2={height / 2}
            stroke="currentColor"
            strokeWidth={1}
            strokeDasharray="2 2"
          />
        </svg>
      </div>
    );
  }

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      className={cn('overflow-visible', className)}
      role="img"
      aria-label={`Sparkline chart showing ${resolvedTrend} trend`}
    >
      {showArea && pathData.area && (
        <path d={pathData.area} fill={colors.fill} strokeWidth={0} />
      )}
      {pathData.line && (
        <path
          d={pathData.line}
          fill="none"
          stroke={colors.stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

export interface SparklineTrendProps {
  trend: 'up' | 'down' | 'neutral';
  className?: string;
}

export function SparklineTrend({ trend, className }: SparklineTrendProps) {
  const icon = React.useMemo(() => {
    switch (trend) {
      case 'up':
        return '↗';
      case 'down':
        return '↘';
      default:
        return '→';
    }
  }, [trend]);

  const colorClass = React.useMemo(() => {
    switch (trend) {
      case 'up':
        return 'text-emerald-600';
      case 'down':
        return 'text-rose-600';
      default:
        return 'text-slate-500';
    }
  }, [trend]);

  return (
    <span
      className={cn('text-sm font-medium', colorClass, className)}
      aria-label={`Trend: ${trend}`}
    >
      {icon}
    </span>
  );
}
